import React from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import {
  Calendar,
  Activity,
  BarChart3,
  Target,
  Clock,
  TrendingUp,
  CheckCircle2,
  Download,
  ArrowRight,
  Menu,
  Play,
} from "lucide-react";
import "./App.css";
import Terms from "./Terms";
import Privacy from "./Privacy";

// Custom Button component
const Button = ({
  children,
  variant = "default",
  size = "default",
  className = "",
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background";

  const variants = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    destructive:
      "bg-destructive text-destructive-foreground hover:bg-destructive/90",
    outline: "border border-input hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    link: "underline-offset-4 hover:underline text-primary",
  };

  const sizes = {
    default: "h-10 py-2 px-4",
    sm: "h-9 px-3 rounded-md",
    lg: "h-11 px-8 rounded-md",
    icon: "h-10 w-10",
  };

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${className}`;

  return (
    <button className={classes} {...props}>
      {children}
    </button>
  );
};

// Custom Card component
const Card = ({ children, className = "", ...props }) => {
  return (
    <div
      className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Custom Badge component
const Badge = ({ children, className = "", ...props }) => {
  return (
    <div
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

// Home component
function Home() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="flex items-center justify-between px-6 py-4 md:px-12">
        <Link to="/" className="flex items-center space-x-2">
          <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center">
            <Activity className="w-4 h-4 text-white" />
          </div>
          <span className="text-xl font-medium text-black">Sana</span>
        </Link>
        <div className="hidden md:flex items-center space-x-8">
          <a
            href="#features"
            className="text-black hover:opacity-70 transition-opacity"
          >
            Features
          </a>
          <a
            href="#benefits"
            className="text-black hover:opacity-70 transition-opacity"
          >
            Benefits
          </a>
          <a
            href="#download"
            className="text-black hover:opacity-70 transition-opacity"
          >
            Download
          </a>
        </div>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="w-5 h-5" />
        </Button>
      </nav>

      {/* Hero Section */}
      <section className="px-6 py-16 md:px-12 md:py-24 text-center">
        <div className="max-w-4xl mx-auto">
          <Badge className="mb-6 bg-gray-100 text-black hover:bg-gray-200 animate-fade-in-up">
            Biohacking Made Simple
          </Badge>
          <h1 className="text-4xl md:text-6xl mb-6 text-black hero-title animate-fade-in-up">
            <span className="font-light">Transform your</span>
            <br />
            <span className="font-medium">longevity journey</span>
          </h1>
          <p className="text-lg md:text-xl text-black opacity-70 mb-8 max-w-2xl mx-auto">
            Schedule and track your progress with proven biohacking protocols.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button className="btn-primary text-white rounded-full px-8 py-3 flex items-center gap-2 hover-lift">
              <Play className="w-4 h-4" />
              Watch Demo
            </Button>
            <Button
              variant="outline"
              className="border-black text-black hover:bg-gray-50 rounded-full px-8 py-3 flex items-center gap-2 hover-lift"
            >
              Learn More
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="bg-white rounded-[20px] p-4 flex items-center space-x-3">
              <div className="bg-green-100 rounded-full w-8 h-8 flex items-center justify-center">
                <Clock className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-black font-medium">
                Intermittent Fasting
              </span>
            </div>
            <div className="bg-white rounded-[20px] p-4 flex items-center space-x-3">
              <div className="bg-blue-100 rounded-full w-8 h-8 flex items-center justify-center">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-black font-medium">Cold Therapy</span>
            </div>
            <div className="bg-white rounded-[20px] p-4 flex items-center space-x-3">
              <div className="bg-purple-100 rounded-full w-8 h-8 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-purple-600" />
              </div>
              <span className="text-black font-medium">HRV Training</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="px-6 py-16 md:px-12 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl mb-4 text-black">
              <span className="font-light">Everything you need to</span>
              <br />
              <span className="font-medium">optimize your biology</span>
            </h2>
            <p className="text-lg text-black opacity-70 max-w-2xl mx-auto">
              Scientifically-backed protocols designed to enhance your longevity
              and daily performance.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-8 border-gray-200 card-hover">
              <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <Calendar className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-xl font-medium text-black mb-3">
                Smart Scheduling
              </h3>
              <p className="text-black opacity-70">
                Personalized protocol schedules that adapt to your lifestyle and
                optimize for maximum effectiveness.
              </p>
            </Card>

            <Card className="p-8 border-gray-200 card-hover">
              <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-medium text-black mb-3">
                Progress Tracking
              </h3>
              <p className="text-black opacity-70">
                Comprehensive analytics to monitor your biomarkers, habits, and
                long-term health improvements.
              </p>
            </Card>

            <Card className="p-8 border-gray-200 card-hover">
              <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-medium text-black mb-3">
                Protocol Library
              </h3>
              <p className="text-black opacity-70">
                Access to evidence-based biohacking protocols from leading
                longevity researchers and practitioners.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        id="benefits"
        className="px-6 py-16 md:px-12 md:py-24 bg-gray-50"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl mb-6 text-black">
                <span className="font-light">Build habits that</span>
                <br />
                <span className="font-medium">extend your healthspan</span>
              </h2>
              <p className="text-lg text-black opacity-70 mb-8">
                Join thousands of biohackers who are using Sana to optimize
                their longevity protocols and track meaningful improvements in
                their health markers.
              </p>
              <div className="space-y-4">
                {[
                  "Increase energy and mental clarity",
                  "Improve sleep quality and recovery",
                  "Enhance metabolic health markers",
                  "Build sustainable longevity habits",
                ].map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span className="text-black">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:pl-12">
              <Card className="p-8 border-gray-200">
                <div className="text-center space-y-6">
                  <div className="bg-black rounded-full w-16 h-16 flex items-center justify-center mx-auto">
                    <TrendingUp className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <div className="text-3xl font-medium text-black mb-2">
                      4.8/5
                    </div>
                    <p className="text-black opacity-70">Average user rating</p>
                  </div>
                  <div className="grid grid-cols-2 gap-6 pt-4 border-t border-gray-200">
                    <div className="text-center">
                      <div className="text-2xl font-medium text-black">92%</div>
                      <p className="text-sm text-black opacity-70">
                        Improved sleep
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-medium text-black">87%</div>
                      <p className="text-sm text-black opacity-70">
                        Higher energy
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="download" className="px-6 py-16 md:px-12 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl mb-6 text-black">
            <span className="font-light">Start your</span>
            <br />
            <span className="font-medium">longevity journey today</span>
          </h2>
          <p className="text-lg text-black opacity-70 mb-8 max-w-2xl mx-auto">
            Download Sana and begin implementing science-backed protocols that
            will optimize your health and extend your lifespan.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button className="btn-primary text-white rounded-full px-8 py-3 flex items-center gap-2 hover-lift">
              <Download className="w-4 h-4" />
              Download for iOS
            </Button>
            <Button
              variant="outline"
              className="border-black text-black hover:bg-gray-50 rounded-full px-8 py-3 flex items-center gap-2 hover-lift"
            >
              <Download className="w-4 h-4" />
              Download for Android
            </Button>
          </div>
          <p className="text-sm text-black opacity-50 mt-4">
            Free to download • Premium features available
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-12 md:px-12 border-t border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="bg-black rounded-full w-8 h-8 flex items-center justify-center">
                <Activity className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-medium text-black">Sana</span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-black opacity-70">
              <Link
                to="/privacy"
                className="hover:opacity-100 transition-opacity"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                className="hover:opacity-100 transition-opacity"
              >
                Terms
              </Link>
              <Link
                to="/support"
                className="hover:opacity-100 transition-opacity"
              >
                Support
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-black opacity-50">
              © 2024 Sana. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Main App component with routing
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
